/**
 * `parseAttackLine` (P0-11b deliverable 1): reads one 2014-style attack
 * description ("Melee Weapon Attack: +4 to hit, reach 5 ft., one target.
 * Hit: 5 (1d6 + 2) slashing damage.") and returns an {@link AttackV1}. It
 * never throws.
 *
 * Grammar (in order):
 *   <kind>: <toHit> to hit, <distance>, <target>. Hit: <hitClause>
 *
 * `<hitClause>` is a primary damage part, optionally followed by one
 * versatile alternative ("or … if used with two hands", with an optional
 * thrown-weapon tail, "… if used with two hands to make a melee attack"),
 * one conditional
 * alternative ("or … if …", kept as a second `damage[]` entry with a
 * `note`), any number of riders ("plus … damage"), and free descriptive
 * text ("hitText"). A hit clause that starts with no leading damage number
 * is entirely descriptive: `damage` stays empty and the whole clause
 * becomes `hitText`.
 */
import { DAMAGE_TYPES } from '../../schema/common.js';
/** A sign glyph accepted for a to-hit or damage bonus: ASCII +/-, or U+2212. */
const SIGN = '[+−-]';
const ATTACK_KIND_RE = new RegExp('^(Melee or Ranged Weapon Attack|Melee Weapon Attack|Melee Spell Attack|' +
    'Ranged Weapon Attack|Ranged Spell Attack):\\s*');
const TO_HIT_RE = new RegExp(`^(${SIGN})\\s*(\\d+)\\s*to hit,\\s*`);
const REACH_ONLY_RE = /^reach (\d+) ft\.,\s*/;
const RANGE_ONLY_RE = /^range (\d+)(?:\/(\d+))? ft\.,\s*/;
const REACH_OR_RANGE_RE = /^reach (\d+) ft\. or range (\d+)(?:\/(\d+))? ft\.,\s*/;
const TARGET_RE = /^([^.]+)\.\s*Hit:\s*/;
const DICE_DAMAGE_RE = new RegExp(`^(\\d+)\\s*\\((\\d+)d(\\d+)(?:\\s*(${SIGN})\\s*(\\d+))?\\)\\s*([a-z]+)\\s+damage\\b`);
const FLAT_DAMAGE_RE = /^(\d+)\s*([a-z]+)\s+damage\b/;
const VERSATILE_RE = new RegExp(`^,?\\s*or\\s+(\\d+)\\s*\\((\\d+)d(\\d+)(?:\\s*(${SIGN})\\s*(\\d+))?\\)\\s*` +
    '([a-z]+)\\s+damage if used with two hands(?: to make a melee attack)?\\b');
const CONDITIONAL_DICE_RE = new RegExp(`^,?\\s*or\\s+(\\d+)\\s*\\((\\d+)d(\\d+)(?:\\s*(${SIGN})\\s*(\\d+))?\\)\\s*` +
    '([a-z]+)\\s+damage\\s+((?:if|in melee or)[^.]*)');
const CONDITIONAL_FLAT_RE = /^,?\s*or\s+(\d+)\s*([a-z]+)\s+damage\s+((?:if|in melee or)[^.]*)/;
const RIDER_DICE_RE = new RegExp(`^,?\\s*plus\\s+(\\d+)\\s*\\((\\d+)d(\\d+)(?:\\s*(${SIGN})\\s*(\\d+))?\\)\\s*([a-z]+)\\s+damage\\b`);
const RIDER_FLAT_RE = /^,?\s*plus\s+(\d+)\s*([a-z]+)\s+damage\b/;
const LEADING_JUNCTION_RE = /^[\s.,;]+/;
const VALID_DICE = new Set([4, 6, 8, 10, 12, 20, 100]);
function signOf(glyph) {
    return glyph === '+' ? 1 : -1;
}
function isDamageType(value) {
    return DAMAGE_TYPES.includes(value);
}
/** Strips the sign/bonus digits of a dice-damage regex match starting at group `offset`. */
function readDiceDamage(m, offset) {
    const average = Number(m[offset]);
    const count = Number(m[offset + 1]);
    const die = Number(m[offset + 2]);
    const bonusSign = m[offset + 3];
    const bonus = bonusSign ? signOf(bonusSign) * Number(m[offset + 4]) : 0;
    const type = String(m[offset + 5]);
    return { average, count, die, bonus, type };
}
/** `d.die` has already been checked against {@link VALID_DICE}. */
function dice(d) {
    return { count: d.count, die: d.die, bonus: d.bonus };
}
function cleanupLeading(text) {
    return text.replace(LEADING_JUNCTION_RE, '');
}
/** Strips exactly one trailing period; the formatter re-adds it at the very end. */
function stripTrailingPeriod(text) {
    return text.endsWith('.') ? text.slice(0, -1) : text;
}
export function parseAttackLine(text) {
    if (typeof text !== 'string')
        return { ok: false, reason: 'not_a_string' };
    // Open5e's `desc` may wrap the attack kind in Markdown emphasis
    // (`*Melee Weapon Attack:*`); 2014 attack text never otherwise carries a
    // literal asterisk or underscore, so stripping them globally is safe.
    let rest = text.replace(/[*_]/g, '');
    const kindMatch = ATTACK_KIND_RE.exec(rest);
    if (!kindMatch || kindMatch[1] === undefined)
        return { ok: false, reason: 'no_attack_kind' };
    const kindText = kindMatch[1];
    rest = rest.slice(kindMatch[0].length);
    const range = kindText === 'Melee or Ranged Weapon Attack'
        ? 'meleeOrRanged'
        : kindText.startsWith('Melee')
            ? 'melee'
            : 'ranged';
    const kind = kindText.includes('Spell') ? 'spell' : 'weapon';
    const toHitMatch = TO_HIT_RE.exec(rest);
    if (!toHitMatch)
        return { ok: false, reason: 'no_to_hit' };
    const toHitSign = toHitMatch[1];
    const toHitDigits = toHitMatch[2];
    if (toHitSign === undefined || toHitDigits === undefined) {
        return { ok: false, reason: 'no_to_hit' };
    }
    const toHit = signOf(toHitSign) * Number(toHitDigits);
    rest = rest.slice(toHitMatch[0].length);
    let reach = null;
    let rangeNormal = null;
    let rangeLong = null;
    if (range === 'melee') {
        const m = REACH_ONLY_RE.exec(rest);
        if (!m || m[1] === undefined)
            return { ok: false, reason: 'no_distance' };
        reach = Number(m[1]);
        rest = rest.slice(m[0].length);
    }
    else if (range === 'ranged') {
        const m = RANGE_ONLY_RE.exec(rest);
        if (!m || m[1] === undefined)
            return { ok: false, reason: 'no_distance' };
        rangeNormal = Number(m[1]);
        rangeLong = m[2] !== undefined ? Number(m[2]) : null;
        rest = rest.slice(m[0].length);
    }
    else {
        const m = REACH_OR_RANGE_RE.exec(rest);
        if (!m || m[1] === undefined || m[2] === undefined)
            return { ok: false, reason: 'no_distance' };
        reach = Number(m[1]);
        rangeNormal = Number(m[2]);
        rangeLong = m[3] !== undefined ? Number(m[3]) : null;
        rest = rest.slice(m[0].length);
    }
    const targetMatch = TARGET_RE.exec(rest);
    if (!targetMatch || targetMatch[1] === undefined)
        return { ok: false, reason: 'no_hit_clause' };
    const target = targetMatch[1];
    rest = rest.slice(targetMatch[0].length);
    const warnings = [];
    const damage = [];
    let versatile = null;
    const riders = [];
    const diceMatch = DICE_DAMAGE_RE.exec(rest);
    if (diceMatch) {
        const d = readDiceDamage(diceMatch, 1);
        if (!VALID_DICE.has(d.die))
            return { ok: false, reason: 'invalid_die' };
        if (!isDamageType(d.type))
            return { ok: false, reason: 'unknown_damage_type' };
        damage.push({ average: d.average, dice: dice(d), flat: null, type: d.type, note: null });
        rest = rest.slice(diceMatch[0].length);
    }
    else {
        const flatMatch = FLAT_DAMAGE_RE.exec(rest);
        if (flatMatch && flatMatch[1] !== undefined && flatMatch[2] !== undefined) {
            const flat = Number(flatMatch[1]);
            const type = flatMatch[2];
            if (isDamageType(type)) {
                damage.push({ average: null, dice: null, flat, type, note: null });
                rest = rest.slice(flatMatch[0].length);
            }
            // An unrecognised type falls through: the whole clause becomes hitText.
        }
    }
    if (damage.length > 0) {
        // Only one of a versatile alternative or a conditional alternative is
        // recognised per line (no real 2014 line carries both).
        const vMatch = VERSATILE_RE.exec(rest);
        if (vMatch) {
            const d = readDiceDamage(vMatch, 1);
            if (VALID_DICE.has(d.die) && isDamageType(d.type)) {
                versatile = { average: d.average, dice: dice(d), flat: null, type: d.type, note: null };
                rest = rest.slice(vMatch[0].length);
            }
        }
        else {
            const cMatch = CONDITIONAL_DICE_RE.exec(rest);
            if (cMatch) {
                const d = readDiceDamage(cMatch, 1);
                const note = cMatch[7];
                if (VALID_DICE.has(d.die) && isDamageType(d.type) && note !== undefined) {
                    damage.push({
                        average: d.average,
                        dice: dice(d),
                        flat: null,
                        type: d.type,
                        note: note.trim(),
                    });
                    rest = rest.slice(cMatch[0].length);
                }
            }
            else {
                const cfMatch = CONDITIONAL_FLAT_RE.exec(rest);
                if (cfMatch &&
                    cfMatch[1] !== undefined &&
                    cfMatch[2] !== undefined &&
                    cfMatch[3] !== undefined) {
                    const flat = Number(cfMatch[1]);
                    const type = cfMatch[2];
                    const note = cfMatch[3];
                    if (isDamageType(type)) {
                        damage.push({ average: null, dice: null, flat, type, note: note.trim() });
                        rest = rest.slice(cfMatch[0].length);
                    }
                }
            }
        }
        // Any number of "plus … damage" riders.
        for (;;) {
            const rDice = RIDER_DICE_RE.exec(rest);
            if (rDice) {
                const d = readDiceDamage(rDice, 1);
                if (!VALID_DICE.has(d.die) || !isDamageType(d.type))
                    break;
                riders.push({ average: d.average, dice: dice(d), flat: null, type: d.type, note: null });
                rest = rest.slice(rDice[0].length);
                continue;
            }
            const rFlat = RIDER_FLAT_RE.exec(rest);
            if (rFlat && rFlat[1] !== undefined && rFlat[2] !== undefined && isDamageType(rFlat[2])) {
                riders.push({
                    average: null,
                    dice: null,
                    flat: Number(rFlat[1]),
                    type: rFlat[2],
                    note: null,
                });
                rest = rest.slice(rFlat[0].length);
                continue;
            }
            break;
        }
    }
    const hitTextSource = stripTrailingPeriod(cleanupLeading(rest));
    const hitText = hitTextSource.length > 0 ? hitTextSource : null;
    const attack = {
        range,
        kind,
        toHit,
        reach,
        rangeNormal,
        rangeLong,
        target,
        damage,
        versatile,
        riders,
        hitText,
    };
    return { ok: true, attack, rest: '', warnings };
}
//# sourceMappingURL=parse.js.map